import { useEffect, useState } from "react";
import {
    FiRefreshCcw,
    FiDollarSign,
    FiClock,
    FiCheckCircle,
    FiSearch,
    FiLoader,
    FiX,
    FiTrendingDown,
    FiAlertCircle,
} from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useDebouncedCallback } from "use-debounce";
import {
    fetchRefundOverview,
    processOrderRefund,
    type CreditOrder,
} from "../../store/smileDesign/paymentSlice";
import TablePagination from "../../components/smileDesign/TablePagination";

const RefundManager = () => {
    const dispatch = useAppDispatch();
    const {
        refunds,
        refundableOrders,
        refundStats,
        refundLoading,
        refundsPagination,
        refundablePagination,
        error,
    } = useAppSelector((state) => state.payment);

    const [search, setSearch] = useState("");
    const [refundsPage, setRefundsPage] = useState(1);
    const [refundablePage, setRefundablePage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<CreditOrder | null>(
        null
    );
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const loadRefunds = (overrides?: {
        search?: string;
        refundsPage?: number;
        refundablePage?: number;
    }) => {
        dispatch(
            fetchRefundOverview({
                search: (overrides?.search ?? search) || undefined,
                refundsPage: overrides?.refundsPage ?? refundsPage,
                refundablePage: overrides?.refundablePage ?? refundablePage,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setRefundsPage(1);
        setRefundablePage(1);
        loadRefunds({ search: value, refundsPage: 1, refundablePage: 1 });
    }, 400);

    useEffect(() => {
        loadRefunds();
    }, []);

    const handleSearch = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const openRefundModal = (order?: CreditOrder) => {
        setSelectedOrder(order ?? null);
        setReason("");
        setModalOpen(true);
    };

    const handleRefund = async () => {
        if (!selectedOrder || !reason.trim()) return;

        setSubmitting(true);
        try {
            await dispatch(
                processOrderRefund({
                    order_id: selectedOrder.order_id,
                    reason: reason.trim(),
                })
            ).unwrap();
            setModalOpen(false);
            loadRefunds();
        } catch {
            // error in slice
        } finally {
            setSubmitting(false);
        }
    };

    const formatAmount = (amount: number) => {
        if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
        return amount.toFixed(2);
    };

    const formatDate = (date: string) =>
        new Date(date).toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });

    return (
        <div className="min-h-screen bg-[#080808] text-white p-4 sm:p-6">
            <div className="w-full">
                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-5 mb-8">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                            Refund Manager
                        </h1>
                        <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                            Process Stripe refunds and revoke credits from paid
                            orders.
                        </p>
                    </div>

                    <button
                        onClick={() => openRefundModal()}
                        disabled={refundableOrders.length === 0}
                        className="flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 transition-all duration-200 text-white px-6 py-3 rounded-2xl shadow-lg font-medium"
                    >
                        <FiRefreshCcw />
                        Process Refund
                    </button>
                </div>

                {error && (
                    <div className="mb-6 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-red-400 text-sm">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">
                                    Total Refunds
                                </p>
                                <h2 className="text-3xl font-bold text-cyan-400 mt-2">
                                    {refundStats?.totalRefunds ?? 0}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400">
                                <FiRefreshCcw className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">
                                    Refunded Amount
                                </p>
                                <h2 className="text-3xl font-bold text-red-400 mt-2">
                                    {formatAmount(
                                        refundStats?.refundedAmount ?? 0
                                    )}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                                <FiDollarSign className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">
                                    Refundable Orders
                                </p>
                                <h2 className="text-3xl font-bold text-yellow-400 mt-2">
                                    {refundStats?.pendingRefundable ?? 0}
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center text-yellow-400">
                                <FiClock className="text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#101211] border border-[#1f2221] rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#8b8f8d] text-sm">
                                    Refund Rate
                                </p>
                                <h2 className="text-3xl font-bold text-purple-400 mt-2">
                                    {refundStats?.refundRate ?? 0}%
                                </h2>
                            </div>
                            <div className="w-14 h-14 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400">
                                <FiTrendingDown className="text-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative mb-6">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d]" />
                    <input
                        type="text"
                        placeholder="Search by order, customer, or package..."
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                </div>

                {refundLoading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className="animate-spin text-cyan-400 text-4xl" />
                    </div>
                )}

                {!refundLoading && refundableOrders.length > 0 && (
                    <div className="mb-8">
                        <h2 className="text-lg font-semibold text-white mb-4">
                            Eligible for Refund
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {refundableOrders.map((order) => (
                                <div
                                    key={order.order_id}
                                    className="bg-[#101211] border border-yellow-500/20 rounded-2xl p-5"
                                >
                                    <div className="flex justify-between items-start gap-3">
                                        <div>
                                            <p className="text-cyan-400 font-semibold">
                                                #{order.order_id}
                                            </p>
                                            <p className="text-white font-medium mt-1">
                                                {order.user_name}
                                            </p>
                                            <p className="text-[#8b8f8d] text-sm">
                                                {order.package_name}
                                            </p>
                                        </div>
                                        <span className="text-green-400 font-bold">
                                            {order.currency}
                                            {order.amount}
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => openRefundModal(order)}
                                        className="mt-4 w-full py-2.5 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-500 hover:text-black transition font-medium"
                                    >
                                        Refund Order
                                    </button>
                                </div>
                            ))}
                        </div>

                        <TablePagination
                            pagination={refundablePagination}
                            onPageChange={(nextPage) => {
                                setRefundablePage(nextPage);
                                loadRefunds({ refundablePage: nextPage });
                            }}
                            itemLabel="refundable orders"
                        />
                    </div>
                )}

                {!refundLoading && refunds.length === 0 && (
                    <div className="text-center py-20 rounded-3xl border border-[#1f2221] bg-[#101211]">
                        <FiRefreshCcw className="text-5xl text-[#3a3d3c] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold">No refunds yet</h3>
                        <p className="text-[#8b8f8d] mt-2 text-sm">
                            Completed refunds will appear here.
                        </p>
                    </div>
                )}

                {!refundLoading && refunds.length > 0 && (
                    <>
                        <h2 className="text-lg font-semibold text-white mb-4">
                            Refund History
                        </h2>

                        <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-[#080808] border-b border-[#1f2221]">
                                        <tr>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Order
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Customer
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Amount
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Credits
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Reason
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Status
                                            </th>
                                            <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                                Date
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {refunds.map((refund) => (
                                            <tr
                                                key={refund.order_id}
                                                className="border-b border-[#1f2221] hover:bg-[#151716] transition-all duration-200"
                                            >
                                                <td className="px-6 py-5 font-medium text-cyan-400">
                                                    #{refund.order_id}
                                                </td>
                                                <td className="px-6 py-5">
                                                    <h3 className="font-semibold text-white">
                                                        {refund.user_name}
                                                    </h3>
                                                    <p className="text-[#8b8f8d] text-sm">
                                                        {refund.email}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className="text-red-400 font-bold">
                                                        {refund.currency}
                                                        {refund.amount}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5 text-cyan-400">
                                                    {refund.total_credits ??
                                                        refund.credits +
                                                            refund.bonus_credits}
                                                </td>
                                                <td className="px-6 py-5 text-[#b7bab8] max-w-xs truncate">
                                                    {(refund.refund_reason) ?? "—"}
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border bg-green-500/10 border-green-500/20 text-green-400">
                                                        <FiCheckCircle />
                                                        Refunded
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5 text-[#8b8f8d]">
                                                    {formatDate(refund.updated_at)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="lg:hidden space-y-4">
                            {refunds.map((refund) => (
                                <div
                                    key={refund.order_id}
                                    className="bg-[#101211] border border-[#1f2221] rounded-3xl p-5"
                                >
                                    <div className="flex justify-between gap-4">
                                        <div>
                                            <p className="text-cyan-400 font-semibold">
                                                #{refund.order_id}
                                            </p>
                                            <h3 className="font-semibold text-white mt-2">
                                                {refund.user_name}
                                            </h3>
                                            <p className="text-[#8b8f8d] text-sm">
                                                {refund.email}
                                            </p>
                                        </div>
                                        <span className="text-red-400 font-bold">
                                            {refund.currency}
                                            {refund.amount}
                                        </span>
                                    </div>
                                    <p className="text-[#8b8f8d] text-sm mt-4">
                                        {refund.refund_reason ?? "No reason recorded"}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden mt-4">
                            <TablePagination
                                pagination={refundsPagination}
                                onPageChange={(nextPage) => {
                                    setRefundsPage(nextPage);
                                    loadRefunds({ refundsPage: nextPage });
                                }}
                                itemLabel="refunds"
                            />
                        </div>
                    </>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
                    <div className="w-full max-w-md bg-[#101211] border border-[#1f2221] rounded-3xl p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold">Process Refund</h2>
                            <button
                                onClick={() => setModalOpen(false)}
                                className="text-[#8b8f8d] hover:text-white"
                            >
                                <FiX className="text-xl" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-[#8b8f8d] mb-2 block">
                                    Select Order
                                </label>
                                <select
                                    value={selectedOrder?.order_id ?? ""}
                                    onChange={(e) => {
                                        const order = refundableOrders.find(
                                            (o) =>
                                                o.order_id ===
                                                Number(e.target.value)
                                        );
                                        setSelectedOrder(order ?? null);
                                    }}
                                    className="w-full bg-[#080808] border border-[#1f2221] rounded-xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                >
                                    <option value="">Choose a paid order</option>
                                    {refundableOrders.map((order) => (
                                        <option
                                            key={order.order_id}
                                            value={order.order_id}
                                        >
                                            #{order.order_id} — {order.user_name}{" "}
                                            ({order.currency}
                                            {order.amount})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="text-sm text-[#8b8f8d] mb-2 block">
                                    Refund Reason
                                </label>
                                <textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g. Duplicate payment, customer request..."
                                    rows={3}
                                    className="w-full bg-[#080808] border border-[#1f2221] rounded-xl py-3 px-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                                />
                            </div>

                            {selectedOrder && (
                                <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-sm text-yellow-200/90 flex gap-3">
                                    <FiAlertCircle className="shrink-0 mt-0.5" />
                                    <p>
                                        This will issue a full Stripe refund and
                                        revoke{" "}
                                        {selectedOrder.total_credits ??
                                            selectedOrder.credits +
                                                selectedOrder.bonus_credits}{" "}
                                        credits from {selectedOrder.user_name}.
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleRefund}
                                disabled={
                                    submitting ||
                                    !selectedOrder ||
                                    !reason.trim()
                                }
                                className="w-full py-3 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {submitting ? (
                                    <FiLoader className="animate-spin" />
                                ) : (
                                    <FiRefreshCcw />
                                )}
                                Confirm Refund
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RefundManager;
